import React,{useRef,useState,useEffect,useMemo} from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import './shop.css'
import './events.css'

/* ------------------------------------------------------------------
   ICONS
   ------------------------------------------------------------------ */
const IconPlus=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>)
const IconEdit=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>)
const IconX=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>)
const IconSearch=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>)
const IconCheck=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12.5l5 5L20 6.5"/></svg>)
const IconQr=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" rx="1"/>
    <rect x="14" y="3" width="7" height="7" rx="1"/>
    <rect x="3" y="14" width="7" height="7" rx="1"/>
    <path d="M14 14h3v3h-3zM20 14v3M14 20h6"/>
  </svg>
)
const IconPeople=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="9" cy="8" r="3.2"/>
    <path d="M3 20a6 6 0 0 1 12 0"/>
    <path d="M16.5 5.3a3.2 3.2 0 0 1 0 5.4"/>
    <path d="M18 14.6A6 6 0 0 1 21 20"/>
  </svg>
)

/* ------------------------------------------------------------------
   THEME TOKENS FOR THE DATA GRID — see EShopProducts for the why.
   ------------------------------------------------------------------ */
const TOKEN_NAMES=[
  '--panel','--panel-raise','--line','--line-soft',
  '--ink','--ink-2','--ink-3','--accent','--accent-wash',
  '--mono','--sans','--step-0'
]

const readTokens=()=>{
  const out={}
  if (typeof window==='undefined'||!document.documentElement) return out
  const styles=window.getComputedStyle(document.documentElement)
  TOKEN_NAMES.forEach(name=>{ out[name]=String(styles.getPropertyValue(name)||'').trim() })
  return out
}

function useTableStyles(){
  const [tokens,setTokens]=useState(readTokens)

  useEffect(()=>{
    let frame=null
    const refresh=()=>{
      if (frame) window.cancelAnimationFrame(frame)
      frame=window.requestAnimationFrame(()=>setTokens(readTokens()))
    }

    const observer=new MutationObserver(refresh)
    observer.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme','class','style']})

    let media=null
    if (window.matchMedia){
      media=window.matchMedia('(prefers-color-scheme: dark)')
      if (media.addEventListener) media.addEventListener('change',refresh)
      else if (media.addListener) media.addListener(refresh)
    }

    refresh()

    return ()=>{
      if (frame) window.cancelAnimationFrame(frame)
      observer.disconnect()
      if (media){
        if (media.removeEventListener) media.removeEventListener('change',refresh)
        else if (media.removeListener) media.removeListener(refresh)
      }
    }
  },[])

  return useMemo(()=>{
    const t=tokens
    return {
      table:{ style:{ backgroundColor:t['--panel'], color:t['--ink'] } },
      tableWrapper:{ style:{ display:'block' } },
      head:{ style:{ color:t['--ink-3'] } },
      headRow:{
        style:{
          backgroundColor:t['--panel-raise'],
          color:t['--ink-3'],
          minHeight:'42px',
          borderBottomWidth:'1px',
          borderBottomColor:t['--line'],
          borderBottomStyle:'solid'
        }
      },
      headCells:{
        style:{
          paddingLeft:'12px',
          paddingRight:'12px',
          fontFamily:t['--mono'],
          fontSize:'10px',
          fontWeight:600,
          letterSpacing:'0.08em',
          textTransform:'uppercase',
          color:t['--ink-3']
        }
      },
      rows:{
        style:{
          backgroundColor:t['--panel'],
          color:t['--ink'],
          fontFamily:t['--sans'],
          fontSize:t['--step-0']||'0.875rem',
          minHeight:'52px',
          '&:not(:last-of-type)':{
            borderBottomWidth:'1px',
            borderBottomStyle:'solid',
            borderBottomColor:t['--line-soft']
          }
        },
        highlightOnHoverStyle:{
          backgroundColor:t['--accent-wash'],
          color:t['--ink'],
          borderBottomColor:t['--line-soft'],
          outlineWidth:'0px',
          outlineColor:t['--panel'],
          transitionDuration:'0.15s',
          transitionProperty:'background-color'
        }
      },
      cells:{ style:{ paddingLeft:'12px', paddingRight:'12px' } },
      pagination:{
        style:{
          backgroundColor:t['--panel'],
          color:t['--ink-3'],
          fontSize:'12px',
          minHeight:'50px',
          borderTopWidth:'1px',
          borderTopStyle:'solid',
          borderTopColor:t['--line']
        },
        pageButtonsStyle:{
          color:t['--ink-2'],
          fill:t['--ink-2'],
          backgroundColor:'transparent',
          '&:disabled':{ color:t['--ink-3'], fill:t['--ink-3'] },
          '&:hover:not(:disabled)':{ backgroundColor:t['--panel-raise'] },
          '&:focus':{ outline:'none', backgroundColor:t['--accent-wash'] }
        }
      },
      noData:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'], padding:'26px' } },
      progress:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'] } }
    }
  },[tokens])
}

/* Token-styled dialog. Escape closes, backdrop closes, full-bleed under 560px.
   `compact` opts a dialog into the height-bounded roster layout in events.css:
   the sheet stops growing, the backdrop stops scrolling, and the body becomes a
   flex column whose one flexible child scrolls. The edit form does not pass it
   and is rendered exactly as before. */
function Sheet({open,title,eyebrow,subtitle,size,compact,onClose,children}){
  useEffect(()=>{
    if (!open) return undefined
    const onKey=(e)=>{ if (e.key==='Escape') onClose() }
    document.addEventListener('keydown',onKey)
    return ()=>document.removeEventListener('keydown',onKey)
  },[open,onClose])

  /* Its own effect, keyed on `open` alone. onClose is a fresh arrow on every
     render of the screen, so the effect above tears down and rebuilds on each
     keystroke — a body-scroll lock in there would read back its own 'hidden'
     as the value to restore and leave the page frozen after close. */
  useEffect(()=>{
    if (!open) return undefined
    const prevOverflow=document.body.style.overflow
    document.body.style.overflow='hidden'
    return ()=>{ document.body.style.overflow=prevOverflow }
  },[open])

  if (!open) return null

  return (
    <div
      className={compact?'sh-back ev-back':'sh-back'}
      onMouseDown={(e)=>{ if (e.target===e.currentTarget) onClose() }}
    >
      <div
        className={['sh-modal',size||'',compact?'ev-sheet':''].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="sh-modal-head">
          <div className="sh-modal-headline">
            {eyebrow?<span className="sh-modal-eyebrow">{eyebrow}</span>:null}
            <h2 className="sh-modal-title">{title}</h2>
            {subtitle?<div className="sh-modal-sub">{subtitle}</div>:null}
          </div>
          <button type="button" className="sh-modal-x" onClick={onClose} aria-label="Κλείσιμο"><IconX/></button>
        </div>
        <div className="sh-modal-body">{children}</div>
      </div>
    </div>
  )
}

/* "2025-04-12T00:00:00.000Z" -> "2025-04-12"; anything else passes through. */
const dayOf=(value)=>String(value===undefined||value===null?'':value).split('T')[0]

const BLANK={ModalTitle:'ΝΕΑ ΕΓΓΡΑΦΗ', id:0,Title:'',IsActive:1,Descr:'',Location:'',DateFrom:'',DateTo:'',SportID:0,Tags:''}

/* Live = still running AND the switch is on. Same test as before. */
const liveOf=(row)=>{
  const today=new Date().toISOString().split('T')[0]
  return dayOf(row&&row.DateTo)<=today ? 0 : (row?row.IsActive:0)
}

const toneOf=(row)=>{
  if (liveOf(row)!=1) return 'crit'
  return (row&&row.AcceptParticipants==1) ? 'good' : 'warn'
}

const stateLabel=(row)=>{
  const tone=toneOf(row)
  if (tone==='good') return 'Ενεργό — δέχεται συμμετοχές'
  if (tone==='warn') return 'Ενεργό — κλειστές συμμετοχές'
  return 'Ανενεργό'
}

export default function Events() {
    const [data,setData]=useState([])
    const [dataFlt,setDataFlt]=useState([])
    const [isLoading,setIsLoading]=useState(true)
    const [show, setShow] = useState(false);
    const [show2, setShow2] = useState(false);
    const [show3, setShow3] = useState(false);
    const [record,setRecord]=useState(BLANK)
    const [sports,setSports]=useState([])
    const [barcode,setBarcode]=useState('')
    const [person,setPerson]=useState('')
    const [tag,setTag]=useState('')
    const [personUUID,setPersonUUID]=useState('')
    const [scanError,setScanError]=useState(false)
    const [attendees,setAttendees]=useState([])
    const [selSportID,setSelSportID]=useState(0)
    const inputRef = useRef(null);

    /* inline feedback — this is what replaced alert() / window.confirm() */
    const [note,setNote]=useState(null)
    const [formNote,setFormNote]=useState(null)
    const [askDelete,setAskDelete]=useState(false)
    const [unreg,setUnreg]=useState(null)
    const [q,setQ]=useState('')

    const tableStyles=useTableStyles()

    const columns = [
        {
            name: '#',
            selector: (row,index) =>  index+1,
            sortable: false,
            width:'56px'
        },
        {
            name: 'Ενεργό',
            selector: row => toneOf(row),
            cell: row => (
                <span className="sh-status" title={stateLabel(row)}>
                    <span className="sh-dot" data-tone={toneOf(row)}></span>
                    <span className="sh-status-t">{stateLabel(row)}</span>
                </span>
            ),
            sortable: true,
            minWidth:'220px'
        },
        {
            name: 'Τίτλος',
            selector: row =>  row.Title,
            cell: row => <span className="sh-cell-strong">{row.Title}</span>,
            sortable: true,
            minWidth:'180px'
        },
        {
            name: 'Τοποθεσία',
            selector: row =>  row.Location,
            sortable: true,
            minWidth:'150px'
        },
        {
            name: 'Sport',
            selector: row =>  row.SportName,
            sortable: true,
            minWidth:'130px'
        },
        {
            name: 'Έναρξη',
            selector: row =>  dayOf(row.DateFrom),
            sortable: true,
            width:'130px'
        },
        {
            name: 'Λήξη',
            selector: row =>  dayOf(row.DateTo),
            sortable: true,
            width:'130px'
        },
        {
            name: '',
            cell: row => (
                <span className="sh-rowbar">
                    <button type="button" className="sh-icon-btn" onClick={()=>showModal2(row.id)} aria-label={'Scan — '+String(row.Title||'')}><IconQr/></button>
                    <button type="button" className="sh-icon-btn" onClick={()=>showModal3(row.id)} aria-label={'Attendees — '+String(row.Title||'')}><IconPeople/></button>
                    <button type="button" className="sh-icon-btn" onClick={()=>showModal(row.id)} aria-label={'Edit — '+String(row.Title||'')}><IconEdit/></button>
                </span>
            ),
            sortable: false,
            width:'156px',
            right:true
        }
    ]


    const columns2 = [
        {
            name: '#',
            selector: (row,index) =>  index+1,
            sortable: false,
            width:'56px'
        },
        {
            name: 'Date',
            selector: row =>  row.ScanDate,
            sortable: true,
            width:'130px'
        },
        {
          name: 'Time',
          selector: row =>  row.ScanTime,
          sortable: true,
          width:'110px'
        },
        {
          name: 'Attendee',
          selector: row =>  row.FirstName +' '+row.LastName,
          cell: row => <span className="sh-cell-strong">{row.FirstName+' '+row.LastName}</span>,
          sortable: true,
          minWidth:'180px'
        },
        {
          name: 'Category',
          selector: row =>  row.Tag,
          sortable: true,
          minWidth:'130px'
        }
    ]


    const columns3 = [
        {
            name: '#',
            selector: (row,index) =>  index+1,
            sortable: false,
            width:'56px'
        },
        {
          name: 'Attendee',
          selector: row =>  row.FirstName +' '+row.LastName,
          cell: row => <span className="sh-cell-strong">{row.FirstName+' '+row.LastName}</span>,
          sortable: true,
          minWidth:'180px'
        },
        {
          name: 'Registration Date',
          selector: row =>  row.RegisteredDate,
          sortable: true,
          minWidth:'160px'
        },
        {
          name: 'Category',
          selector: row =>  row.Tag,
          sortable: true,
          minWidth:'130px'
        },
        {
          name: '',
          cell: row => (
            <button
              type="button"
              className="sh-icon-btn"
              data-tone="crit"
              onClick={()=>setUnreg(row)}
              aria-label={'Unregister — '+String(row.FirstName||'')+' '+String(row.LastName||'')}
            >
              <IconX/>
            </button>
          ),
          sortable: false,
          width:'70px',
          right:true
        }
    ]

    const unregisterUser=(UserID,EventID)=>{
            axios.delete(process.env.REACT_APP_API_SERVER+'admin/events/delattendee/'+UserID+'/'+EventID,{
                headers: {
                    'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                    }
                })
              .then(res=>{
                if (res.data.err==0){
                    loadEventAttendees(record.UUID)
                    setUnreg(null)
                    setFormNote({tone:'ok',text:res.data.msg})
                }else{
                    setUnreg(null)
                    setFormNote({tone:'crit',text:res.data.msg})
                }
            }).catch(err=>{
                setUnreg(null)
                setFormNote({tone:'crit',text:'Η διαγραφή συμμετοχής απέτυχε. Δοκιμάστε ξανά.'})
                console.log(err)
            })
    }

    const handleClose = () => {
        setShow(false)
        setPerson('')
        setAskDelete(false)
        setFormNote(null)
        setRecord(BLANK)
    }

    const handleClose2 = () => {
        setPersonUUID('')
        setScanError(false)
        setBarcode('')
        setPerson('')
        setShow2(false)
        setFormNote(null)
        setRecord(BLANK)
    }

    const handleClose3 = () => {
        setPersonUUID('')
        setScanError(false)
        setBarcode('')
        setPerson('')
        setShow3(false)
        setUnreg(null)
        setFormNote(null)
        setRecord(BLANK)
    }


      useEffect(()=>{
        loadData()
        loadSports()
      },[])





      const loadData=()=>{
        setIsLoading(true)
        axios.get(process.env.REACT_APP_API_SERVER+'admin/events',{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{
          setData(res.data.results)
          setDataFlt(res.data.results)
          setIsLoading(false)

        }).catch(err=>{
            setIsLoading(false)
            setNote({tone:'crit',text:'Τα events δεν φορτώθηκαν. Ελέγξτε τη σύνδεση και δοκιμάστε ξανά.'})
            console.log(err)
        })
      }


        const loadSports=()=>{
          axios.get(process.env.REACT_APP_API_SERVER+'admin/sports',{
            headers: {
                'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                }
            })
          .then(res=>{
            setSports(res.data.results)
          }).catch(err=>{
              console.log(err)
          })
        }



    const loadAttendees=(uuid)=>{

        axios.get(process.env.REACT_APP_API_SERVER+'admin/events/attendees/'+uuid+'/1',{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{
            setAttendees(res.data.results)

        }).catch(err=>{
            setFormNote({tone:'crit',text:'Η λίστα σαρώσεων δεν φορτώθηκε.'})
            console.log(err)
        })
    }

    const loadEventAttendees=(uuid)=>{

        axios.get(process.env.REACT_APP_API_SERVER+'admin/events/attendees/'+uuid+'/0',{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{
            setAttendees(res.data.results)

        }).catch(err=>{
            setFormNote({tone:'crit',text:'Η λίστα συμμετεχόντων δεν φορτώθηκε.'})
            console.log(err)
        })
    }


      const showModal=(id)=>{
        setFormNote(null)
        setAskDelete(false)
        if (id==0){
            setRecord({ModalTitle:'ΝΕΑ ΕΓΓΡΑΦΗ', id:0,Title:'',IsActive:1,Descr:'',Location:'',DateFrom:'',DateTo:'',SportID:0,Tags:'',AcceptParticipants:1})
            setShow(true)
        }else{
            const recObj=data.filter(item=>item.id==id)[0]
            if (!recObj) return

            setRecord({ModalTitle:recObj.Title, id:recObj.id,Title:recObj.Title,Descr:recObj.Descr,Location:recObj.Location,DateFrom:recObj.DateFrom,DateTo:recObj.DateTo,IsActive:recObj.IsActive,SportID:recObj.SportID,Tags:recObj.Tags,AcceptParticipants:recObj.AcceptParticipants})
            setShow(true)
        }
      }

      const showModal2=(id)=>{
            setScanError(false)
            setBarcode('')
            setPerson('')
            setPersonUUID('')
            setFormNote(null)
            const recObj=data.filter(item=>item.id==id)[0]
            if (!recObj) return

            setRecord({ModalTitle:recObj.Title, id:recObj.id,Title:recObj.Title,Descr:recObj.Descr,Location:recObj.Location,DateFrom:recObj.DateFrom,DateTo:recObj.DateTo,IsActive:recObj.IsActive,SportID:recObj.SportID,Tags:recObj.Tags,UUID:recObj.UUID,AcceptParticipants:recObj.AcceptParticipants})
            setAttendees([])
            loadAttendees(recObj.UUID)

            setShow2(true)
      }


      const showModal3=(id)=>{
        setScanError(false)
        setBarcode('')
        setPerson('')
        setPersonUUID('')
        setFormNote(null)
        setUnreg(null)
        const recObj=data.filter(item=>item.id==id)[0]
        if (!recObj) return

        setRecord({ModalTitle:recObj.Title, id:recObj.id,Title:recObj.Title,Descr:recObj.Descr,Location:recObj.Location,DateFrom:recObj.DateFrom,DateTo:recObj.DateTo,IsActive:recObj.IsActive,SportID:recObj.SportID,Tags:recObj.Tags,UUID:recObj.UUID,AcceptParticipants:recObj.AcceptParticipants})
        setAttendees([])
        loadEventAttendees(recObj.UUID)

        setShow3(true)
  }


      const selectSport=(sportID)=>{
        setSelSportID(sportID)
        if (sportID>0){
            setDataFlt(data.filter(item=>item.SportID==sportID))
        }else{
            setDataFlt(data)
        }

      }

      const sportsList=sports.map(item=>{
        return(
          <option key={item.SportID} value={item.SportID}>{item.SportName}</option>
        )
      })

      const save=(e)=>{
        e.preventDefault()

        axios.post(process.env.REACT_APP_API_SERVER+'admin/events',record,{
            headers: {
                'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                }
            })
          .then(res=>{
            if (res.data.err==0){
                loadData()
                setShow(false)
                setNote({tone:'ok',text:res.data.msg})
            }else{
                setFormNote({tone:'crit',text:res.data.msg})
            }
        }).catch(err=>{
            setFormNote({tone:'crit',text:'Η αποθήκευση απέτυχε. Δοκιμάστε ξανά.'})
            console.log(err)
        })
      }

      const deleteRecord=()=>{
          axios.delete(process.env.REACT_APP_API_SERVER+'admin/events/delevent/'+record.id,{
              headers: {
                  'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                  }
              })
            .then(res=>{
              if (res.data.err==0){
                  loadData()
                  setShow(false)
                  setAskDelete(false)
                  setNote({tone:'ok',text:res.data.msg})
              }else{
                  setFormNote({tone:'crit',text:res.data.msg})
              }
          }).catch(err=>{
              setFormNote({tone:'crit',text:'Η διαγραφή απέτυχε. Δοκιμάστε ξανά.'})
              console.log(err)
          })
      }

      const scan=(e)=>{
        e.preventDefault()
        axios.get(process.env.REACT_APP_API_SERVER+'admin/events/scan/'+barcode+'/'+record.UUID,{
            headers: {
                'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                }
            })
          .then(res=>{
            if (res.data.err==0){
                loadAttendees(record.UUID)
                setPerson(res.data.person)
                setTag(res.data.tag)
                setPersonUUID(res.data.userUUID)
                setBarcode('')
                setScanError(false)
                inputRef.current?.focus()
            }else{
                setScanError(true)
                setPersonUUID('')
                setBarcode('')
                setPerson('')
                inputRef.current?.focus()
            }

        }).catch(err=>{
            setScanError(true)
            setPersonUUID('')
            setPerson('')
            inputRef.current?.focus()
            console.log(err)
        })
      }

      /* The scanned attendee's photo.
         This used to be a loadImage(id) helper called inline in the JSX, which
         stamped a fresh Date.now() on EVERY render. The scan panel re-renders on
         every keystroke in the barcode field, so the face refetched and flickered
         while the operator was typing the next barcode. The stamp is now computed
         once per UUID. An absent UUID yields '' — and '' is never handed to an
         <img>, because src="" resolves to the SPA document, which can never
         decode as an image. */
      const personPhoto=useMemo(()=>{
        const id=String(personUUID==null?'':personUUID).trim()
        if (!id) return ''
        return `${process.env.REACT_APP_API_SERVER}files/${id}/${Date.now()}`
      },[personUUID])

      const base=Array.isArray(dataFlt)?dataFlt:[]
      const total=Array.isArray(data)?data.length:0
      const needle=q.trim().toLowerCase()
      const shown=needle
        ? base.filter(item=>(String(item.Title||'')+' '+String(item.Location||'')+' '+String(item.SportName||'')).toLowerCase().indexOf(needle)>=0)
        : base

      const scanned=Array.isArray(attendees)?attendees.length:0

      return (
        <div className="sh-page">

            {note
              ? <div className="sh-note" data-tone={note.tone} role="status">
                  <span className="sh-note-text">{note.text}</span>
                  <button type="button" className="sh-note-x" aria-label="Κλείσιμο" onClick={()=>setNote(null)}><IconX/></button>
                </div>
              : null}

            <Panel
              title="Events"
              subtitle={isLoading
                ? 'Φόρτωση…'
                : total===0
                  ? 'Κανένα event'
                  : total+(total===1?' event':' events')+' συνολικά'}
              actions={
                <div className="sh-tools">
                  {total>0
                    ? <>
                        <select
                          className="sh-filter"
                          value={selSportID}
                          aria-label="Φίλτρο αθλήματος"
                          onChange={(e)=>selectSport(e.target.value)}
                        >
                          <option value={0}>[All Sports]</option>
                          {sportsList}
                        </select>
                        <span className="sh-search">
                          <IconSearch/>
                          <input
                            className="sh-search-input"
                            type="search"
                            value={q}
                            placeholder="Τίτλος ή τοποθεσία"
                            aria-label="Αναζήτηση event"
                            onChange={(e)=>setQ(e.target.value)}
                          />
                        </span>
                      </>
                    : null}
                  <Btn variant="accent" onClick={()=>showModal(0)}><IconPlus/><span>Νέο event</span></Btn>
                </div>
              }
            >
              {isLoading
                ? <div className="sh-sk" aria-hidden="true">
                    <span className="skeleton"></span>
                    <span className="skeleton"></span>
                    <span className="skeleton"></span>
                    <span className="skeleton"></span>
                    <span className="skeleton"></span>
                  </div>
                : total===0
                  ? <div className="sh-empty">
                      <div className="sh-empty-t">Κανένα event ακόμη</div>
                      <div className="sh-empty-s">
                        Ένα event δέχεται εγγραφές από την εφαρμογή και στην είσοδο οι συμμετέχοντες
                        σαρώνονται με barcode.
                      </div>
                      <div className="sh-empty-a">
                        <Btn variant="accent" onClick={()=>showModal(0)}><IconPlus/><span>Νέο event</span></Btn>
                      </div>
                    </div>
                  : shown.length===0
                    ? <div className="sh-empty">
                        <div className="sh-empty-t">Κανένα event δεν ταιριάζει</div>
                        <div className="sh-empty-s">Καθαρίστε τα φίλτρα για να δείτε και τα {total} events.</div>
                        <div className="sh-empty-a">
                          <Btn onClick={()=>{setQ('');selectSport(0)}}>Καθαρισμός φίλτρων</Btn>
                        </div>
                      </div>
                    : <>
                        <div className="sh-count">
                          <span className="sh-count-n num">{shown.length}</span>
                          <span>{needle||String(selSportID)!=='0'?'events που ταιριάζουν με τα φίλτρα':'events στη λίστα'}</span>
                        </div>
                        <div className="sh-table">
                          <DataTable
                            columns={columns}
                            data={shown}
                            customStyles={tableStyles}
                            highlightOnHover
                            persistTableHead
                            pagination
                            paginationPerPage={25}
                            paginationRowsPerPageOptions={[25,50,100,200]}
                            noDataComponent={<div className="sh-empty-s">Κανένα event.</div>}
                          />
                        </div>
                      </>}
            </Panel>

            {/* ---------------- edit / create ---------------- */}
            <Sheet
              open={show}
              size="sh-modal-lg"
              eyebrow={record.id>0?'Επεξεργασία event':'Νέο event'}
              title={record.ModalTitle}
              onClose={handleClose}
            >
              {formNote
                ? <div className="sh-note" data-tone={formNote.tone} role="alert">
                    <span className="sh-note-text">{formNote.text}</span>
                    <button type="button" className="sh-note-x" aria-label="Κλείσιμο" onClick={()=>setFormNote(null)}><IconX/></button>
                  </div>
                : null}

              <form className="sh-form" onSubmit={save}>

                <fieldset className="sh-fieldset">
                  <legend className="sh-legend">Τι και πού</legend>

                  <div className="sh-field">
                    <label className="sh-label" htmlFor="ev-title">Title</label>
                    <input
                      id="ev-title"
                      className="sh-input"
                      type="text"
                      maxLength={250}
                      required
                      value={record.Title}
                      onChange={(e)=>setRecord({...record,Title:e.target.value})}
                    />
                  </div>

                  <div className="sh-field">
                    <label className="sh-label" htmlFor="ev-descr">Short Description</label>
                    <textarea
                      id="ev-descr"
                      className="sh-textarea"
                      rows={3}
                      maxLength={2000}
                      value={record.Descr===undefined||record.Descr===null?'':record.Descr}
                      onChange={(e)=>setRecord({...record,Descr:e.target.value})}
                    />
                  </div>

                  <div className="sh-field">
                    <label className="sh-label" htmlFor="ev-loc">Location</label>
                    <input
                      id="ev-loc"
                      className="sh-input"
                      type="text"
                      maxLength={250}
                      required
                      value={record.Location}
                      onChange={(e)=>setRecord({...record,Location:e.target.value})}
                    />
                  </div>
                </fieldset>

                <fieldset className="sh-fieldset">
                  <legend className="sh-legend">Πότε</legend>

                  <div className="sh-pair">
                    <div className="sh-field">
                      <label className="sh-label" htmlFor="ev-from">Date From</label>
                      <input
                        id="ev-from"
                        className="sh-input"
                        type="date"
                        required
                        value={dayOf(record.DateFrom)}
                        onChange={(e)=>setRecord({...record,DateFrom:e.target.value})}
                      />
                    </div>

                    <div className="sh-field">
                      <label className="sh-label" htmlFor="ev-to">Date To</label>
                      <input
                        id="ev-to"
                        className="sh-input"
                        type="date"
                        required
                        value={dayOf(record.DateTo)}
                        onChange={(e)=>setRecord({...record,DateTo:e.target.value})}
                      />
                    </div>
                  </div>
                </fieldset>

                <fieldset className="sh-fieldset">
                  <legend className="sh-legend">Κατηγοριοποίηση</legend>

                  <div className="sh-pair">
                    <div className="sh-field">
                      <label className="sh-label" htmlFor="ev-sport">Sport</label>
                      <select
                        id="ev-sport"
                        className="sh-select"
                        required
                        value={record.SportID}
                        onChange={(e)=>setRecord({...record,SportID:e.target.value})}
                      >
                        <option value=''>[Select Sport]</option>
                        {sportsList}
                      </select>
                    </div>

                    <div className="sh-field">
                      <label className="sh-label" htmlFor="ev-tags">Categories</label>
                      <input
                        id="ev-tags"
                        className="sh-input"
                        type="text"
                        maxLength={250}
                        required
                        value={record.Tags}
                        onChange={(e)=>setRecord({...record,Tags:e.target.value})}
                      />
                      <div className="sh-hint">comma seperated — πχ. Elite, Amateur, Staff</div>
                    </div>
                  </div>
                </fieldset>

                <fieldset className="sh-fieldset">
                  <legend className="sh-legend">Ορατότητα</legend>

                  <div className="sh-pair">
                    <div className="sh-field">
                      <label className="sh-label" htmlFor="ev-active">Ενεργό</label>
                      <select
                        id="ev-active"
                        className="sh-select"
                        value={record.IsActive}
                        onChange={(e)=>setRecord({...record,IsActive:e.target.value})}
                      >
                        <option value='1'>ΝΑΙ</option>
                        <option value='0'>ΟΧΙ</option>
                      </select>
                    </div>

                    <div className="sh-field">
                      <label className="sh-label" htmlFor="ev-accept">Δέχεται Συμμετοχές</label>
                      <select
                        id="ev-accept"
                        className="sh-select"
                        value={record.AcceptParticipants}
                        onChange={(e)=>setRecord({...record,AcceptParticipants:e.target.value})}
                      >
                        <option value='1'>ΝΑΙ</option>
                        <option value='0'>ΟΧΙ</option>
                      </select>
                    </div>
                  </div>
                </fieldset>

                {askDelete
                  ? <div className="sh-confirm" role="group" aria-label="Επιβεβαίωση διαγραφής">
                      <div className="sh-confirm-t">Διαγραφή του event «{record.Title}»;</div>
                      <div className="sh-confirm-s">
                        Το event και οι εγγραφές του φεύγουν από την εφαρμογή. Η ενέργεια δεν αναιρείται.
                      </div>
                      <div className="sh-actions">
                        <Btn onClick={()=>setAskDelete(false)}>Άκυρο</Btn>
                        <span className="sh-actions-end">
                          <Btn className="sh-btn-crit" onClick={deleteRecord}>Οριστική διαγραφή</Btn>
                        </span>
                      </div>
                    </div>
                  : <div className="sh-actions">
                      {record.id>0
                        ? <Btn className="sh-btn-crit" onClick={()=>setAskDelete(true)}>Διαγραφή</Btn>
                        : null}
                      <span className="sh-actions-end">
                        <Btn onClick={handleClose}>Άκυρο</Btn>
                        <Btn variant="accent" type="submit">Αποθήκευση</Btn>
                      </span>
                    </div>}

              </form>
            </Sheet>

            {/* ---------------- scan ---------------- */}
            <Sheet
              open={show2}
              size="sh-modal-xl"
              compact
              eyebrow="Scan"
              title={record.ModalTitle}
              subtitle={record.Location?String(record.Location):undefined}
              onClose={handleClose2}
            >
              <div className="sh-stack">

                <form className="sh-scan" onSubmit={scan}>
                  <div className="sh-field sh-scan-field">
                    <label className="sh-label" htmlFor="ev-barcode">BarCode</label>
                    <input
                      id="ev-barcode"
                      className="sh-input sh-mono"
                      type="text"
                      ref={inputRef}
                      autoFocus={true}
                      autoComplete="off"
                      maxLength={250}
                      required
                      value={barcode}
                      onChange={(e)=>setBarcode(e.target.value)}
                    />
                  </div>
                  <Btn variant="accent" type="submit">Scan</Btn>
                </form>

                {person.length>0
                  ? <div className="sh-hit" role="status">
                      {personPhoto
                        ? <img className="sh-hit-face" src={personPhoto} alt="" width="54" height="54"/>
                        : <span className="sh-hit-face" aria-hidden="true"/>}
                      <div className="sh-hit-body">
                        <div className="sh-hit-n">{person}</div>
                        {tag?<span className="sh-hit-tag">{tag}</span>:null}
                      </div>
                      <span className="sh-hit-ok" aria-hidden="true"><IconCheck/></span>
                    </div>
                  : null}

                {scanError
                  ? <div className="sh-note" data-tone="crit" role="alert">
                      <span className="sh-note-text">Wrong BarCode — ο κωδικός δεν αντιστοιχεί σε συμμετέχοντα αυτού του event.</span>
                      <button type="button" className="sh-note-x" aria-label="Κλείσιμο" onClick={()=>setScanError(false)}><IconX/></button>
                    </div>
                  : null}

                {formNote
                  ? <div className="sh-note" data-tone={formNote.tone} role="alert">
                      <span className="sh-note-text">{formNote.text}</span>
                      <button type="button" className="sh-note-x" aria-label="Κλείσιμο" onClick={()=>setFormNote(null)}><IconX/></button>
                    </div>
                  : null}

                {/* The count stays above the scroll line; only the list moves. */}
                <div className="ev-roster">
                  <div className="sh-meta"><span><b>{scanned}</b> σαρώσεις σε αυτό το event</span></div>
                  {scanned===0
                    ? <div className="sh-empty">
                        <div className="sh-empty-t">Καμία σάρωση ακόμη</div>
                        <div className="sh-empty-s">Σαρώστε το barcode ενός συμμετέχοντα για να καταγραφεί η είσοδός του.</div>
                      </div>
                    : <div className="sh-table scroll-body ev-scroll">
                        <DataTable
                          columns={columns2}
                          data={attendees}
                          customStyles={tableStyles}
                          highlightOnHover
                          persistTableHead
                          pagination
                          paginationPerPage={25}
                          paginationRowsPerPageOptions={[25,50,100,200]}
                          noDataComponent={<div className="sh-empty-s">Καμία σάρωση.</div>}
                        />
                      </div>}
                </div>
              </div>
            </Sheet>

            {/* ---------------- registered users ---------------- */}
            <Sheet
              open={show3}
              size="sh-modal-xl"
              compact
              eyebrow="Registered users"
              title={record.ModalTitle}
              subtitle={record.Location?String(record.Location):undefined}
              onClose={handleClose3}
            >
              <div className="sh-stack">

                {formNote
                  ? <div className="sh-note" data-tone={formNote.tone} role="alert">
                      <span className="sh-note-text">{formNote.text}</span>
                      <button type="button" className="sh-note-x" aria-label="Κλείσιμο" onClick={()=>setFormNote(null)}><IconX/></button>
                    </div>
                  : null}

                {unreg
                  ? <div className="sh-confirm" role="group" aria-label="Επιβεβαίωση διαγραφής συμμετοχής">
                      <div className="sh-confirm-t">
                        Unregister {String(unreg.FirstName||'')+' '+String(unreg.LastName||'')};
                      </div>
                      <div className="sh-confirm-s">
                        Ο συμμετέχων αφαιρείται από αυτό το event και χάνει το barcode εισόδου του.
                        Μπορεί να εγγραφεί ξανά από την εφαρμογή.
                      </div>
                      <div className="sh-actions">
                        <Btn onClick={()=>setUnreg(null)}>Άκυρο</Btn>
                        <span className="sh-actions-end">
                          <Btn className="sh-btn-crit" onClick={()=>unregisterUser(unreg.UserID,unreg.EventID)}>Οριστική αφαίρεση</Btn>
                        </span>
                      </div>
                    </div>
                  : null}

                {/* The count stays above the scroll line; only the list moves. */}
                <div className="ev-roster">
                  <div className="sh-meta"><span><b>{scanned}</b> εγγεγραμμένοι συμμετέχοντες</span></div>
                  {scanned===0
                    ? <div className="sh-empty">
                        <div className="sh-empty-t">Κανένας εγγεγραμμένος</div>
                        <div className="sh-empty-s">Οι εγγραφές γίνονται από τους χρήστες μέσα στην εφαρμογή.</div>
                      </div>
                    : <div className="sh-table scroll-body ev-scroll">
                        <DataTable
                          columns={columns3}
                          data={attendees}
                          customStyles={tableStyles}
                          highlightOnHover
                          persistTableHead
                          pagination
                          paginationPerPage={25}
                          paginationRowsPerPageOptions={[25,50,100,200]}
                          noDataComponent={<div className="sh-empty-s">Κανένας συμμετέχων.</div>}
                        />
                      </div>}
                </div>
              </div>
            </Sheet>
        </div>
      )
}
